import { api } from '@/lib/http-client';
import type { TenantWithRole } from '@/types/auth';
import type {
  AddMemberRequest,
  Member,
  TenantLogoUpload,
  TenantResource,
  UpdateMemberRoleRequest,
  UpdateTenantRequest,
  UploadTenantLogoRequest,
} from '@/types/settings';

/**
 * Módulo 1 — Tenants y miembros (`docs/openapi.yaml` → tag `Tenants`).
 * Todo lo que va bajo `/api/tenants/:tenantId/...` exige membresía (el backend responde
 * 403 si no lo eres, 404 si el recurso es de otro tenant).
 *
 * La gestión de miembros (listar/agregar/cambiar rol/eliminar) es solo ADMIN: el backend
 * protege esas rutas con `requireRole('ADMIN')`, incluido el GET.
 */

/** `GET /api/tenants` — membresías del usuario (con su rol en cada tenant). */
export function listTenants(): Promise<TenantWithRole[]> {
  return api.get<TenantWithRole[]>('/api/tenants');
}

export interface CreateTenantRequest {
  nombreEmpresa: string;
  logoUrl?: string;
}

/** `POST /api/tenants` — crea un tenant; el usuario queda como ADMIN. */
export function createTenant(payload: CreateTenantRequest): Promise<TenantWithRole> {
  return api.post<TenantWithRole>('/api/tenants', payload);
}

/** `GET /api/tenants/:tenantId` — fila del negocio (nombre, logo; las API keys van cifradas). */
export function fetchTenant(tenantId: string): Promise<TenantResource> {
  return api.get<TenantResource>(`/api/tenants/${tenantId}`);
}

/** `PATCH /api/tenants/:tenantId` (solo ADMIN; al menos un campo). */
export function updateTenant(
  tenantId: string,
  payload: UpdateTenantRequest,
): Promise<TenantResource> {
  return api.patch<TenantResource>(`/api/tenants/${tenantId}`, payload);
}

/**
 * `POST /api/tenants/:tenantId/logo` (solo ADMIN) — sube UN archivo de logo y devuelve su URL.
 *
 * El archivo NO se guarda en la fila del negocio aquí: igual que las fotos del catálogo, la subida
 * es independiente del `PATCH`, así que la URL que devuelve hay que mandarla después en
 * `PATCH /api/tenants/:tenantId` (`logoUrl`). El backend acepta hasta 5 MB y detecta el formato por
 * firma binaria (JPEG, PNG, WEBP, GIF o HEIC); si el archivo no es una imagen, responde 400 con el
 * motivo. El logo se guarda como `logos/<tenantId>/logo.<ext>` y reemplaza al anterior.
 */
export function uploadTenantLogo(
  tenantId: string,
  payload: UploadTenantLogoRequest,
): Promise<TenantLogoUpload> {
  return api.post<TenantLogoUpload>(`/api/tenants/${tenantId}/logo`, payload);
}

/** `GET /api/tenants/:tenantId/users` (solo ADMIN) — miembros con su rol. */
export function listMembers(tenantId: string): Promise<Member[]> {
  return api.get<Member[]>(`/api/tenants/${tenantId}/users`);
}

/**
 * `POST /api/tenants/:tenantId/users` (solo ADMIN) — agrega a un usuario EXISTENTE
 * (el registro crea la cuenta; este endpoint solo crea la membresía). 404 si el email
 * todavía no tiene cuenta, 409 si ya es miembro.
 */
export function addMember(tenantId: string, payload: AddMemberRequest): Promise<Member> {
  return api.post<Member>(`/api/tenants/${tenantId}/users`, payload);
}

/**
 * `PATCH /api/tenants/:tenantId/users/:userId` (solo ADMIN).
 * El backend impide degradar al último administrador del tenant (400).
 */
export function updateMemberRole(
  tenantId: string,
  userId: string,
  payload: UpdateMemberRoleRequest,
): Promise<Member> {
  return api.patch<Member>(`/api/tenants/${tenantId}/users/${userId}`, payload);
}

/** `DELETE /api/tenants/:tenantId/users/:userId` (solo ADMIN; no deja el tenant sin ADMIN). */
export function removeMember(
  tenantId: string,
  userId: string,
): Promise<{ removed: boolean }> {
  return api.delete<{ removed: boolean }>(`/api/tenants/${tenantId}/users/${userId}`);
}
